/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Button, Description, Input, Label, Sidebar } from "@nordcloud/gnui";
import { useUpdateEnvironmentMutation } from "~/generated/graphql";
import { Fieldset, ContactsInput, LoaderWrap } from "~/components";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { showError, showSuccess } from "~/services/toast";
import { generateActionSuccessText } from "~/tools";
import { Env } from "~/views/applications/AddApplicationPage/types";
import { SwitchEnvType } from "~/views/applications/components/SwitchEnvType";
import { FormValues } from "./types";
import { EnvironmentSchema } from "./validators";

type Props = {
  environment: Env;
};

export function EditEnvironmentSidebar({ environment }: Props) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,

    formState: { errors },
  } = useForm({
    resolver: yupResolver(EnvironmentSchema),
  });

  const [updateEnvironment, { loading: loadingUpdate }] =
    useUpdateEnvironmentMutation();

  React.useEffect(() => {
    register("name", { required: true });
    register("type", { required: true });
    register("contactId");
    register("description");
  }, [register]);

  React.useEffect(() => {
    setValue("contactId", environment.contacts?.[0].id ?? "");
    setValue("type", environment.environmentType);
    setValue("name", environment.name);
    setValue("description", environment.description);
  }, [environment, setValue]);

  const onContactChange = (contact: string) => {
    try {
      setValue("contactId", [contact]);
      showSuccess(SUCCESS_TEXT.contactPersonAdded);
    } catch {
      showError(ERROR_TEXT.default);
    }
  };

  const onUpdate = async (values: FormValues) => {
    try {
      const { contactId, ...restValues } = values;
      await updateEnvironment({
        variables: {
          nid: environment.nid,
          input: {
            ...(contactId ? { contactIds: [contactId] } : {}),
            ...restValues,
          },
        },
      });

      setIsSidebarOpen(false);
      showSuccess(generateActionSuccessText(`Environment `)()("updated")());
    } catch {
      showError(ERROR_TEXT.default);
    }
  };

  const watcher = watch();

  return (
    <>
      <Button
        size="md"
        margin="0.25rem"
        icon="edit"
        severity="low"
        onClick={() => setIsSidebarOpen(true)}
      />
      <Sidebar
        title="Edit Environment"
        isOpen={isSidebarOpen}
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        <LoaderWrap loading={loadingUpdate} inContent>
          <form onSubmit={handleSubmit(onUpdate)}>
            <Fieldset>
              <Label name="Environment Name" required />
              <Input
                type="text"
                {...register("name", { required: true })}
                placeholder="Environment Name"
                defaultValue={watcher.name}
              />
              {errors.name && <Description>Name is required.</Description>}
            </Fieldset>
            <Fieldset>
              <Label name="Select Environment Type" required />
              <SwitchEnvType
                value={watcher.type}
                onChange={(newType) => {
                  setValue("type", newType);
                }}
              />
              {errors.type && <Description>Type is required.</Description>}
            </Fieldset>
            <Fieldset>
              <Label name="Contact Person" />
              <ContactsInput
                placeholder="Contact Person"
                value={[watcher.contactId]}
                status={errors.contactId && "error"}
                onChange={onContactChange}
              />
            </Fieldset>
            <Fieldset>
              <Label name="Environment Description" />
              <Input
                type="text"
                name="description"
                placeholder="Environment Description"
                defaultValue={watcher.description}
              />
            </Fieldset>
            <Button type="submit">Update Environment</Button>
          </form>
        </LoaderWrap>
      </Sidebar>
    </>
  );
}
